import React from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BlockIcon from "@mui/icons-material/Block";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import {
  checkDeliveryAvailability,
  checkOutletDeliveryAvailability,
} from "../redux/userLocationSlice";

/**
 * DeliveryBadge - Shows delivery availability status for a vendor
 *
 * Two modes:
 *  • AUTHORITATIVE (preferred): pass `coverage` — an entry from the backend
 *    coverage cache (useCoverage / useCoverageBatch). The verdict is then the
 *    SAME semantic-policy + road-distance decision the order-create gate makes, so the
 *    badge can never say "Delivers" while checkout 422s. While the check is
 *    loading the badge shows a quiet "Checking…"; if it fails it falls back to
 *    the legacy client-side estimate (marked with "~").
 *  • LEGACY (no `coverage` prop): client-side haversine estimate, unchanged
 *    behaviour for existing call sites.
 *
 * @param {Object} vendor - Vendor object with latitude, longitude, deliveryZoneRadius
 * @param {Object} [coverage] - Backend entry {status, deliverable, distanceKm, radius, deliveryCoverage, coverageCountry, reason}
 * @param {boolean} checkOutlets - Whether to check outlets for delivery (default: false)
 * @param {string} size - Badge size: 'small', 'medium', 'large' (default: 'small')
 * @param {boolean} showDistance - Whether to show distance (default: true)
 */
const DeliveryBadge = ({
  vendor,
  coverage = null,
  checkOutlets = false,
  size = "small",
  showDistance = true,
}) => {
  const userLocation = useSelector((state) => state.userLocation);
  const deliveryCoverage = String(
    coverage?.deliveryCoverage || vendor?.fulfilment?.deliveryCoverage || "",
  ).toUpperCase();
  const isNationwide = deliveryCoverage === "NATIONAL";

  // If no user location, prompt to set one (both modes).
  if (!userLocation.latitude || !userLocation.longitude) {
    return (
      <Badge $status="unknown" $size={size}>
        <LocationOffIcon style={{ fontSize: getFontSize(size) }} />
        <BadgeText $size={size}>Set your location</BadgeText>
      </Badge>
    );
  }

  // ── Authoritative mode ──────────────────────────────────────────────────
  if (coverage) {
    if (coverage.status === "loading" || coverage.status === "idle") {
      return (
        <Badge
          $status="checking"
          $size={size}
          title="Confirming delivery coverage…"
        >
          <LocalShippingIcon style={{ fontSize: getFontSize(size) }} />
          <BadgeText $size={size}>Checking…</BadgeText>
        </Badge>
      );
    }
    if (coverage.status === "succeeded" && coverage.deliverable !== null) {
      const km = Number.isFinite(Number(coverage.distanceKm))
        ? `${Number(coverage.distanceKm).toFixed(1)} km`
        : null;
      const available = coverage.deliverable === true;
      const title = available
        ? isNationwide
          ? `Nationwide delivery${km ? ` (${km} by road from this store)` : ""}`
          : `Delivers to your location${km ? ` (${km} by road)` : ""}`
        : coverage.reason === "OUTSIDE_NATIONAL_COVERAGE"
          ? `Nationwide delivery is limited to ${coverage.coverageCountry || "the store's home country"}`
          : `Outside this vendor's ${coverage.radius ?? "configured"} km delivery zone${km ? ` (${km} by road)` : ""}`;
      return (
        <Badge
          $status={available ? "available" : "unavailable"}
          $size={size}
          title={title}
        >
          {available ? (
            <LocalShippingIcon style={{ fontSize: getFontSize(size) }} />
          ) : (
            <BlockIcon style={{ fontSize: getFontSize(size) }} />
          )}
          <BadgeText $size={size}>
            {available
              ? showDistance && km
                ? `Delivers to you · ${km}`
                : "Delivers"
              : showDistance && km
                ? `Out of range · ${km}`
                : "No delivery"}
          </BadgeText>
        </Badge>
      );
    }
    // failed / undecidable → fall through to the legacy estimate below.
  }

  // ── Legacy client-side estimate ─────────────────────────────────────────
  const deliveryInfo = checkOutlets
    ? checkOutletDeliveryAvailability(vendor, userLocation)
    : checkDeliveryAvailability(vendor, userLocation);

  // If vendor has no location
  if (deliveryInfo.available === null) {
    return null; // Don't show badge if vendor location is not set
  }

  // When this render is a fallback from a failed authoritative check, mark the
  // number as approximate — it's a straight-line estimate, not road distance.
  const approx = coverage ? "~" : "";

  // A failed live distance check must not reinterpret the brand's backend
  // NATIONAL policy as a local radius. The public brand payload is still a
  // backend source of truth; distance remains unavailable until retry.
  if (isNationwide) {
    const estimatedDistance = Number.isFinite(Number(deliveryInfo.distance))
      ? `${approx}${deliveryInfo.distance} km`
      : null;
    return (
      <Badge
        $status="available"
        $size={size}
        title="Nationwide delivery (showing an approximate straight-line distance while the live road-distance check is unavailable)"
      >
        <LocalShippingIcon style={{ fontSize: getFontSize(size) }} />
        <BadgeText $size={size}>
          {showDistance && estimatedDistance
            ? `Delivers to you · ${estimatedDistance}`
            : "Delivers"}
        </BadgeText>
      </Badge>
    );
  }

  return (
    <Badge
      $status={deliveryInfo.available ? "available" : "unavailable"}
      $size={size}
      title={deliveryInfo.message}
    >
      {deliveryInfo.available ? (
        <LocalShippingIcon style={{ fontSize: getFontSize(size) }} />
      ) : (
        <BlockIcon style={{ fontSize: getFontSize(size) }} />
      )}
      <BadgeText $size={size}>
        {deliveryInfo.available
          ? showDistance
            ? `${approx}${deliveryInfo.distance} km`
            : "Delivers"
          : showDistance
            ? `${approx}${deliveryInfo.distance} km away`
            : "No delivery"}
      </BadgeText>
    </Badge>
  );
};

// Helper to get icon font size based on badge size
const getFontSize = (size) => {
  switch (size) {
    case "large":
      return 18;
    case "medium":
      return 14;
    case "small":
    default:
      return 12;
  }
};

export default DeliveryBadge;

// Styled Components — solid colours, no gradients.
const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => (props.$size === "large" ? "6px" : "4px")};
  padding: ${(props) => {
    switch (props.$size) {
      case "large":
        return "8px 14px";
      case "medium":
        return "5px 10px";
      case "small":
      default:
        return "3px 8px";
    }
  }};
  border-radius: 20px;
  font-weight: 600;
  white-space: nowrap;
  margin-top: 6px;

  ${(props) => {
    switch (props.$status) {
      case "available":
        return `
          background: #e9f6ee;
          color: #03660c;
          border: 1px solid #cde9d6;
        `;
      case "unavailable":
        return `
          background: #fdeeee;
          color: #b03a3a;
          border: 1px solid #f3d6d6;
        `;
      case "checking":
        return `
          background: #f2f4f5;
          color: #6b7a80;
          border: 1px solid #e4e9ea;
        `;
      case "unknown":
      default:
        return `
          background: #f2f4f5;
          color: #6b7a80;
          border: 1px solid #e4e9ea;
          cursor: pointer;

          &:hover {
            background: #e9f0f3;
            color: #35525c;
            border-color: #d5e0e4;
          }
        `;
    }
  }}
`;

const BadgeText = styled.span`
  font-size: ${(props) => {
    switch (props.$size) {
      case "large":
        return "0.85rem";
      case "medium":
        return "0.75rem";
      case "small":
      default:
        return "0.65rem";
    }
  }};
  letter-spacing: 0.3px;
`;
